import { CSSInterpolation } from '@mui/material';
import React from 'react';
import { z } from 'zod';

/**
 * Type alias for MUI CSS style objects.
 * Used for consistent typing of style properties across the application.
 */
export type MuiCssStyle = CSSInterpolation;

/**
 * Type for change events from common HTML form elements.
 * Combines event types for input, textarea, and select elements.
 */
export type CustomChangeEvent = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

/**
 * Type for mouse events from button and input elements.
 * Generic type parameter allows specifying the exact element type.
 */
export type CustomMouseEvent<T = HTMLButtonElement | HTMLInputElement> = React.MouseEvent<T>;

/**
 * Type for focus events from any HTML element.
 * Used for handling focus-related interactions.
 */
export type CustomFocusEvent = React.FocusEvent<HTMLElement>;

/**
 * Type for autocomplete component values.
 * Currently accepts any value type for flexibility.
 */
export type AutocompleteValueType = any;

/**
 * Schema for option type objects used in dropdowns and selects.
 * Represents a label-value pair where the value can be either string or number.
 *
 * @property {string} label - Display text for the option
 * @property {string | number} value - Underlying value for the option
 */
export const optionTypeSchema = z.object({
    label: z.string(),
    value: z.union([z.string(), z.number()]),
});

/**
 * TypeScript interface for option type objects.
 * Used for type-checking dropdown and select options.
 */
export type IOptionType = z.infer<typeof optionTypeSchema>;
